import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  User, 
  Subscription, 
  PaymentMethod, 
  Invoice, 
  AccountInfo,
  ChangeEmailRequest,
  ChangePasswordRequest,
  UpdatePaymentMethodRequest
} from '../../services/accountApi';

// State interfaces
export interface AccountState {
  // Account Data
  accountInfo: AccountInfo | null;
  user: User | null;
  subscription: Subscription | null;
  paymentMethods: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod | null;
  billingHistory: Invoice[];
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Dialog States
  isChangeEmailDialogOpen: boolean;
  isChangePasswordDialogOpen: boolean;
  isUpdatePaymentMethodDialogOpen: boolean;
  isCancelSubscriptionDialogOpen: boolean;
  isCloseAccountDialogOpen: boolean;
  
  // Form States and Validation
  changeEmailForm: ChangeEmailRequest;
  changePasswordForm: ChangePasswordRequest;
  updatePaymentMethodForm: UpdatePaymentMethodRequest;
  validationErrors: {
    changeEmail: string[];
    changePassword: string[];
    updatePaymentMethod: string[];
  };
  
  // Success Messages
  successMessages: {
    emailChanged: string;
    passwordChanged: string;
    paymentMethodUpdated: string;
    subscriptionCancelled: string;
  };
  
  // Billing History Pagination
  billingPagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Operation States
  operationLoading: {
    changingEmail: boolean;
    changingPassword: boolean;
    updatingPaymentMethod: boolean;
    cancellingSubscription: boolean;
    closingAccount: boolean;
  };
}

const initialState: AccountState = {
  // Account Data
  accountInfo: null,
  user: null,
  subscription: null,
  paymentMethods: [],
  defaultPaymentMethod: null,
  billingHistory: [],
  
  // UI State
  loading: false,
  error: null,
  
  // Dialog States
  isChangeEmailDialogOpen: false,
  isChangePasswordDialogOpen: false,
  isUpdatePaymentMethodDialogOpen: false,
  isCancelSubscriptionDialogOpen: false,
  isCloseAccountDialogOpen: false,
  
  // Form States
  changeEmailForm: {
    NewEmail: '',
    CurrentPassword: '',
  },
  changePasswordForm: {
    CurrentPassword: '',
    NewPassword: '',
    ConfirmPassword: '',
  },
  updatePaymentMethodForm: {
    CardNumber: '',
    ExpirationMonth: 0,
    ExpirationYear: 0,
    CVC: '',
    CardholderName: '',
  },
  validationErrors: {
    changeEmail: [],
    changePassword: [],
    updatePaymentMethod: [],
  },
  
  // Success Messages
  successMessages: {
    emailChanged: '',
    passwordChanged: '',
    paymentMethodUpdated: '',
    subscriptionCancelled: '',
  },
  
  // Billing History Pagination
  billingPagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Operation States
  operationLoading: {
    changingEmail: false,
    changingPassword: false,
    updatingPaymentMethod: false,
    cancellingSubscription: false,
    closingAccount: false,
  },
};

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Account Data
    setAccountInfo: (state, action: PayloadAction<AccountInfo>) => {
      state.accountInfo = action.payload;
      state.user = action.payload.user;
      state.subscription = action.payload.subscription;
      state.paymentMethods = action.payload.paymentMethods;
      state.defaultPaymentMethod = action.payload.defaultPaymentMethod;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      if (state.accountInfo) {
        state.accountInfo.user = action.payload;
      }
    },
    setSubscription: (state, action: PayloadAction<Subscription | null>) => {
      state.subscription = action.payload;
      if (state.accountInfo) {
        state.accountInfo.subscription = action.payload;
      }
    },
    setPaymentMethods: (state, action: PayloadAction<PaymentMethod[]>) => {
      state.paymentMethods = action.payload;
      if (state.accountInfo) {
        state.accountInfo.paymentMethods = action.payload;
      }
    },
    setDefaultPaymentMethod: (state, action: PayloadAction<PaymentMethod | null>) => {
      state.defaultPaymentMethod = action.payload;
      if (state.accountInfo) {
        state.accountInfo.defaultPaymentMethod = action.payload;
      }
    },
    setBillingHistory: (state, action: PayloadAction<Invoice[]>) => {
      state.billingHistory = action.payload;
    },
    
    // Dialog Actions
    setChangeEmailDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isChangeEmailDialogOpen = action.payload;
      if (!action.payload) {
        state.changeEmailForm = initialState.changeEmailForm;
        state.validationErrors.changeEmail = [];
      }
    },
    setChangePasswordDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isChangePasswordDialogOpen = action.payload;
      if (!action.payload) {
        state.changePasswordForm = initialState.changePasswordForm;
        state.validationErrors.changePassword = [];
      }
    },
    setUpdatePaymentMethodDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isUpdatePaymentMethodDialogOpen = action.payload;
      if (!action.payload) {
        state.updatePaymentMethodForm = initialState.updatePaymentMethodForm;
        state.validationErrors.updatePaymentMethod = [];
      }
    },
    setCancelSubscriptionDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isCancelSubscriptionDialogOpen = action.payload;
    },
    setCloseAccountDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isCloseAccountDialogOpen = action.payload;
    },
    
    // Form Actions
    updateChangeEmailForm: (state, action: PayloadAction<Partial<ChangeEmailRequest>>) => {
      state.changeEmailForm = { ...state.changeEmailForm, ...action.payload };
      // Clear validation errors when user starts typing
      if (state.validationErrors.changeEmail.length > 0) {
        state.validationErrors.changeEmail = [];
      }
    },
    updateChangePasswordForm: (state, action: PayloadAction<Partial<ChangePasswordRequest>>) => {
      state.changePasswordForm = { ...state.changePasswordForm, ...action.payload };
      // Clear validation errors when user starts typing
      if (state.validationErrors.changePassword.length > 0) {
        state.validationErrors.changePassword = [];
      }
    },
    updatePaymentMethodForm: (state, action: PayloadAction<Partial<UpdatePaymentMethodRequest>>) => {
      state.updatePaymentMethodForm = { ...state.updatePaymentMethodForm, ...action.payload };
      // Clear validation errors when user starts typing
      if (state.validationErrors.updatePaymentMethod.length > 0) {
        state.validationErrors.updatePaymentMethod = [];
      }
    },
    
    // Validation
    setChangeEmailValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors.changeEmail = action.payload;
    },
    setChangePasswordValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors.changePassword = action.payload;
    },
    setUpdatePaymentMethodValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors.updatePaymentMethod = action.payload;
    },
    
    // Success Messages
    setSuccessMessage: (state, action: PayloadAction<{ type: keyof AccountState['successMessages']; message: string }>) => {
      state.successMessages[action.payload.type] = action.payload.message;
    },
    clearSuccessMessage: (state, action: PayloadAction<keyof AccountState['successMessages']>) => {
      state.successMessages[action.payload] = '';
    },
    clearAllSuccessMessages: (state) => {
      state.successMessages = initialState.successMessages;
    },
    
    // Billing History Pagination
    setBillingPagination: (state, action: PayloadAction<Partial<AccountState['billingPagination']>>) => {
      state.billingPagination = { ...state.billingPagination, ...action.payload };
    },
    
    // Operation Loading States
    setOperationLoading: (state, action: PayloadAction<{ operation: keyof AccountState['operationLoading']; loading: boolean }>) => {
      state.operationLoading[action.payload.operation] = action.payload.loading;
    },
    
    // Reset state
    resetAccountState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setAccountInfo,
  setUser,
  setSubscription,
  setPaymentMethods,
  setDefaultPaymentMethod,
  setBillingHistory,
  setChangeEmailDialogOpen,
  setChangePasswordDialogOpen,
  setUpdatePaymentMethodDialogOpen,
  setCancelSubscriptionDialogOpen,
  setCloseAccountDialogOpen,
  updateChangeEmailForm,
  updateChangePasswordForm,
  updatePaymentMethodForm,
  setChangeEmailValidationErrors,
  setChangePasswordValidationErrors,
  setUpdatePaymentMethodValidationErrors,
  setSuccessMessage,
  clearSuccessMessage,
  clearAllSuccessMessages,
  setBillingPagination,
  setOperationLoading,
  resetAccountState,
} = accountSlice.actions;

// Saga action types
export const FETCH_ACCOUNT_INFO = 'account/fetchAccountInfo';
export const FETCH_USER_PROFILE = 'account/fetchUserProfile';
export const UPDATE_USER_PROFILE = 'account/updateUserProfile';
export const CHANGE_EMAIL = 'account/changeEmail';
export const CHANGE_PASSWORD = 'account/changePassword';
export const FETCH_SUBSCRIPTION = 'account/fetchSubscription';
export const CANCEL_SUBSCRIPTION = 'account/cancelSubscription';
export const REACTIVATE_SUBSCRIPTION = 'account/reactivateSubscription';
export const FETCH_PAYMENT_METHODS = 'account/fetchPaymentMethods';
export const UPDATE_PAYMENT_METHOD = 'account/updatePaymentMethod';
export const ADD_PAYMENT_METHOD = 'account/addPaymentMethod';
export const DELETE_PAYMENT_METHOD = 'account/deletePaymentMethod';
export const SET_DEFAULT_PAYMENT_METHOD = 'account/setDefaultPaymentMethod';
export const FETCH_BILLING_HISTORY = 'account/fetchBillingHistory';
export const DOWNLOAD_INVOICE = 'account/downloadInvoice';
export const CLOSE_ACCOUNT = 'account/closeAccount';

// Saga action creators
export const fetchAccountInfo = () => ({ type: FETCH_ACCOUNT_INFO });
export const fetchUserProfile = () => ({ type: FETCH_USER_PROFILE });
export const updateUserProfile = (user: Partial<User>) => ({ type: UPDATE_USER_PROFILE, payload: user });
export const changeEmail = (request: ChangeEmailRequest) => ({ type: CHANGE_EMAIL, payload: request });
export const changePassword = (request: ChangePasswordRequest) => ({ type: CHANGE_PASSWORD, payload: request });
export const fetchSubscription = () => ({ type: FETCH_SUBSCRIPTION });
export const cancelSubscription = () => ({ type: CANCEL_SUBSCRIPTION });
export const reactivateSubscription = () => ({ type: REACTIVATE_SUBSCRIPTION });
export const fetchPaymentMethods = () => ({ type: FETCH_PAYMENT_METHODS });
export const updatePaymentMethod = (request: UpdatePaymentMethodRequest) => ({ type: UPDATE_PAYMENT_METHOD, payload: request });
export const addPaymentMethod = (request: UpdatePaymentMethodRequest) => ({ type: ADD_PAYMENT_METHOD, payload: request });
export const deletePaymentMethod = (paymentMethodId: string) => ({ type: DELETE_PAYMENT_METHOD, payload: paymentMethodId });
export const setDefaultPaymentMethodAction = (paymentMethodId: string) => ({ type: SET_DEFAULT_PAYMENT_METHOD, payload: paymentMethodId });
export const fetchBillingHistory = (params?: { page?: number; limit?: number }) => ({ type: FETCH_BILLING_HISTORY, payload: params });
export const downloadInvoice = (invoiceId: string) => ({ type: DOWNLOAD_INVOICE, payload: invoiceId });
export const closeAccount = (reason?: string) => ({ type: CLOSE_ACCOUNT, payload: reason });

export default accountSlice.reducer; 